import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  PhonenumberSetupCostEditForm,
  PhonenumberSetupCostEditInitialValues,
  PhonenumberSetupCostComponents,
  PhonenumberSetupCostAdditionalParams,
  PhonenumberSetupCostFormOptions,
  onPhonenumberSetupCostFormSaved,
  IPhonenumberSetupCostFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedPhonenumberSetupCost, ISharedPatchPhonenumberSetupCost, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchPhonenumberSetupCost, patchPhonenumberSetupCost, getPhonenumberSetupCostUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, usePhonenumberSetupCostParents, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedPhonenumberSetupCost> {}

const PhonenumberSetupCostFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const phonenumber_setup_cost_parents = usePhonenumberSetupCostParents();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchPhonenumberSetupCost(id).then(phonenumber_setup_cost => setValue(phonenumber_setup_cost));
  }, [id, setValue, ]);

  const param_props: IPhonenumberSetupCostFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    phonenumber_setup_cost_parents: phonenumber_setup_cost_parents.phonenumber_setup_cost_parents,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && PhonenumberSetupCostEditForm(value, param_props, t), [value, phonenumber_setup_cost_parents, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && PhonenumberSetupCostFormOptions(value, param_props, t), [value, phonenumber_setup_cost_parents, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchPhonenumberSetupCost, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchPhonenumberSetupCost) => value && save(() => patchPhonenumberSetupCost(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberSetupCostFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!phonenumber_setup_cost_parents.loaded || !organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.PhonenumberSetupCost, value)}
    >
      <AtlasForm<ISharedPatchPhonenumberSetupCost>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={PhonenumberSetupCostComponents}
        additionalParams={PhonenumberSetupCostAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={PhonenumberSetupCostEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('PHONENUMBER_SETUP_COST')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberSetupCostFormEditView = React.memo(withEdit(PhonenumberSetupCostFormEdit, getPhonenumberSetupCostUpdateSchema));
